// O que uma edição do consumo da UC exige do operador, conforme o que ela move.
//
// O problema que isto resolve: editar o consumo de uma UC dispara
// `recalcularCondicaoComercial`, que reescreve `publico` e `multiplicador_final`
// da negociação — e `multiplicador_final` é a primeira fonte da comissão
// (`comissaoExternaV2.ts`, que o chama de "snapshot"). Ou seja, um campo de
// cadastro mexe em dinheiro, e hoje o operador só descobre depois de salvar.
//
// A saída NÃO é bloquear. As UCs 379 (cadastro 137,85 kWh num B3) e 1499
// (cadastro 100) precisam ser corrigidas justamente em negociações antigas —
// bloquear deixaria o dado errado errado para sempre. O que falta é rastro e
// aviso proporcionais ao impacto.
//
// Módulo PURO — sem React, sem rede.
//   node --test src/lib/__tests__/edicaoConsumoUc.test.ts

/** Estado da comissão da negociação a que a UC pertence. */
export type StatusComissao = 'nenhuma' | 'calculada' | 'aprovada' | 'paga';

export type ExigenciaEdicao = 'livre' | 'confirmacao' | 'justificativa' | 'justificativa_com_alerta';

export interface ImpactoEdicaoConsumo {
  exigencia: ExigenciaEdicao;
  /** true quando a edição vai reescrever público/multiplicador da negociação. */
  mexeEmComissao: boolean;
  /** Texto para o operador, sempre no que ele vai causar — não no que o sistema faz. */
  aviso: string | null;
  /** true quando o texto merece tom de alerta, não de informação. */
  grave: boolean;
}

const fmtMult = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toFixed(2).replace('.', ',');

/**
 * Decide o que a edição exige.
 *
 * `publicoAnterior`/`publicoNovo` e os multiplicadores vêm da SIMULAÇÃO
 * (`recalcularCondicaoComercial(id, { simular: true })`), então o texto descreve
 * o efeito real daquela edição específica, não uma possibilidade genérica.
 */
export function avaliarImpactoEdicao({
  consumoMudou,
  condicaoMudaria,
  statusComissao,
  publicoAnterior,
  publicoNovo,
  multiplicadorAnterior,
  multiplicadorNovo,
}: {
  consumoMudou: boolean;
  /** A simulação disse que público ou multiplicador mudam. */
  condicaoMudaria: boolean;
  statusComissao: StatusComissao;
  publicoAnterior?: string | null;
  publicoNovo?: string | null;
  multiplicadorAnterior?: number | null;
  multiplicadorNovo?: number | null;
}): ImpactoEdicaoConsumo {
  // Salvamento que não toca no consumo não pode mexer em comissão. Hoje QUALQUER
  // salvamento da tela dispara o recálculo — corrigir um CEP reclassifica o
  // público. Esta é a guarda que separa as duas coisas.
  if (!consumoMudou) {
    return { exigencia: 'livre', mexeEmComissao: false, aviso: null, grave: false };
  }

  if (!condicaoMudaria) {
    return {
      exigencia: 'livre',
      mexeEmComissao: false,
      aviso: 'O consumo muda, mas a faixa de público e o multiplicador continuam os mesmos.',
      grave: false,
    };
  }

  const mudouPublico = publicoAnterior !== publicoNovo;
  const efeito =
    (mudouPublico ? `público de "${publicoAnterior ?? '—'}" para "${publicoNovo ?? '—'}"` : '') +
    (mudouPublico && multiplicadorAnterior !== multiplicadorNovo ? ' e ' : '') +
    (multiplicadorAnterior !== multiplicadorNovo
      ? `multiplicador de ${fmtMult(multiplicadorAnterior)}× para ${fmtMult(multiplicadorNovo)}×`
      : '');

  if (statusComissao === 'paga') {
    return {
      exigencia: 'justificativa_com_alerta',
      mexeEmComissao: true,
      grave: true,
      aviso:
        `A comissão desta negociação já foi PAGA. Salvar muda ${efeito}, ` +
        'alterando a base de um valor já quitado. Descreva o motivo — o financeiro ' +
        'precisa conseguir reconstruir depois por que o número mudou.',
    };
  }

  if (statusComissao === 'aprovada') {
    return {
      exigencia: 'justificativa',
      mexeEmComissao: true,
      grave: true,
      aviso:
        `A comissão desta negociação já foi aprovada e aguarda pagamento. ` +
        `Salvar muda ${efeito}. Descreva o motivo da correção.`,
    };
  }

  if (statusComissao === 'calculada') {
    return {
      exigencia: 'justificativa',
      mexeEmComissao: true,
      grave: false,
      aviso: `A comissão desta negociação já foi calculada. Salvar muda ${efeito}. Descreva o motivo.`,
    };
  }

  // Sem comissão gerada ainda: a mudança é legítima e rotineira, mas o operador
  // ainda precisa ver o que vai acontecer antes de confirmar.
  return {
    exigencia: 'confirmacao',
    mexeEmComissao: true,
    grave: false,
    aviso: `Salvar muda ${efeito} desta negociação.`,
  };
}

/** Origem do valor gravado em `consumo_medio_kwh`, para o rastro. */
export type OrigemConsumo = 'vendedor' | 'analise' | 'manual' | 'observado';

/**
 * Uma justificativa em branco não serve de rastro. Exigir texto é o que
 * diferencia "alguém corrigiu com motivo" de "alguém digitou por cima".
 */
export const MIN_JUSTIFICATIVA = 10;

export function justificativaValida(texto: string | null | undefined): boolean {
  return (texto || '').trim().length >= MIN_JUSTIFICATIVA;
}

export function exigeJustificativa(exigencia: ExigenciaEdicao): boolean {
  return exigencia === 'justificativa' || exigencia === 'justificativa_com_alerta';
}

/**
 * Estado da comissão da negociação — o que gradua a exigência da edição.
 *
 * Lê `comissao_vendedor_negociacoes` (a linha por negociação) e sobe até o
 * status do fechamento em `comissao_vendedores`. Devolve o estado MAIS AVANÇADO
 * encontrado: uma negociação pode aparecer em mais de um fechamento (spillover
 * entre meses), e o que importa para o aviso é o mais comprometido.
 *
 * Em caso de erro devolve 'nenhuma' — a tela ainda mostra o efeito da mudança
 * pela simulação, e travar o cadastro por falha de leitura seria pior.
 */
export async function buscarStatusComissao(negociacaoId: number | null | undefined): Promise<StatusComissao> {
  if (!negociacaoId) return 'nenhuma';
  try {
    const { supabase } = await import('./supabase');
    const { data, error } = await supabase
      .from('comissao_vendedor_negociacoes')
      .select('comissao_vendedores(status)')
      .eq('negociacao_id', negociacaoId);
    if (error || !data?.length) return 'nenhuma';

    const ordem: StatusComissao[] = ['nenhuma', 'calculada', 'aprovada', 'paga'];
    let pior: StatusComissao = 'nenhuma';
    for (const linha of data as Array<{ comissao_vendedores?: { status?: string } | null }>) {
      const s = linha.comissao_vendedores?.status;
      const norm: StatusComissao =
        s === 'pago' ? 'paga' : s === 'aprovado' ? 'aprovada' : s === 'calculado' ? 'calculada' : 'nenhuma';
      if (ordem.indexOf(norm) > ordem.indexOf(pior)) pior = norm;
    }
    return pior;
  } catch {
    return 'nenhuma';
  }
}
